import React, { useState } from "react";
import { Table, Button, Tag, Input, Select, message, Spin } from "antd";
import { useQuery } from "@tanstack/react-query";
import {
  getOutlets,
  approveOutlet,
  rejectOutlet,
  getAllOutlets,
} from "../actions/acc_actions";
import ContentLayout from "../components/layout/ContentLayout";

const { Option } = Select;

export default function Outlets() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [stampCodesFilter, setStampCodesFilter] = useState([]);

  const { data, isLoading, refetch, error } = useQuery({
    queryKey: [
      "outlets",
      page,
      pageSize,
      statusFilter,
      searchText,
      stampCodesFilter,
    ],
    queryFn: () =>
      getAllOutlets({
        page,
        limit: pageSize,
        status: statusFilter,
        name: searchText,
        " stampCodes[]": stampCodesFilter,
      }),
    keepPreviousData: true,
  });

  const handleApprove = async (urn) => {
    try {
      await approveOutlet(urn);
      message.success("Outlet approved successfully");
      refetch();
    } catch (err) {
      message.error("Failed to approve outlet");
    }
  };

  const handleReject = async (urn) => {
    try {
      await rejectOutlet(urn);
      message.success("Outlet rejected successfully");
      refetch();
    } catch (err) {
      message.error("Failed to reject outlet");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <b>{text}</b>,
    },
    {
      title: "Business Name",
      dataIndex: "business_name",
      key: "business_name",
      render: (text) => <b>{text}</b>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (address) =>
        address
          ? `${address.house_number}, ${address.street_name}, ${address.town}, ${address.postal_code}`
          : "-",
    },
    {
      title: "Stamp Codes",
      dataIndex: "stamp_codes",
      key: "stamp_codes",
      render: (codes) => codes?.join(", "),
    },
    {
      title: "Created",
      dataIndex: "created",
      key: "created",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Status",
      dataIndex: "approve_status",
      key: "approve_status",
      render: (status, record) =>
        status === "PENDING" ? (
          <div style={{ display: "flex", gap: "8px" }}>
            <Button type="primary" onClick={() => handleApprove(record.urn)}>
              Approve
            </Button>
            <Button danger onClick={() => handleReject(record.urn)}>
              Reject
            </Button>
          </div>
        ) : (
          <Tag color={status === "ACCEPTED" ? "green" : "red"}>{status}</Tag>
        ),
    },
  ];

  return (
    <ContentLayout
      header={
        <Spin spinning={isLoading}>
          <h1 style={{ marginBottom: 16 }}>
            <span>Outlets Management</span>
          </h1>
          <div style={{ marginBottom: 16, display: "flex", gap: "8px" }}>
            <Input
              placeholder="Search by name"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={() => setPage(1)}
            />
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: 180 }}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value)}
            >
              <Option value="ACCEPTED">ACCEPTED</Option>
              <Option value="PENDING">PENDING</Option>
              <Option value="REJECTED">REJECTED</Option>
            </Select>
          </div>

          <Table
            rowKey="urn"
            columns={columns}
            dataSource={data?.body?.outlets || []}
            loading={isLoading}
            pagination={{
              current: data?.body?.paginations?.currentPage || page,
              pageSize: data?.body?.paginations?.pageSize || pageSize,
              total: data?.body?.paginations?.totalCount || 0,
              onChange: (newPage, newPageSize) => {
                setPage(newPage);
                setPageSize(newPageSize);
              },
              showSizeChanger: true,
            }}
          />
        </Spin>
      }
    ></ContentLayout>
  );
}
